/**
 * Energy Display Component
 * Shows player's energy level with visual bar and status
 * Enhanced with energy change feedback messages
 */

import React, { useState, useEffect } from 'react';
import { energyManager } from '../engine/EnergyManager';
import { IoFlash, IoTrendingDown, IoClose } from 'react-icons/io5';

interface EnergyDisplayProps {
  energy: number;
  maxEnergy?: number;
  showLabel?: boolean;
  showStatus?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  energyChangeMessage?: string;
  energyChange?: number;
  onEnergyChangeAcknowledged?: () => void;
}

export const EnergyDisplay: React.FC<EnergyDisplayProps> = ({
  energy,
  maxEnergy: _maxEnergy = 100,
  showLabel: _showLabel = true,
  showStatus = true,
  size = 'medium',
  className = '',
  energyChangeMessage,
  energyChange,
  onEnergyChangeAcknowledged
}) => {
  const status = energyManager.getEnergyStatus(energy);
  const [showChangeTooltip, setShowChangeTooltip] = useState(false);

  useEffect(() => {
    // Show tooltip when energy change message is provided and change is significant (±20% or more)
    if (energyChangeMessage && energyChange && Math.abs(energyChange) >= 20) {
      setShowChangeTooltip(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowChangeTooltip(false);
        onEnergyChangeAcknowledged?.();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [energyChangeMessage, energyChange, onEnergyChangeAcknowledged]);

  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg'
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600 bg-green-50 border-green-200';
    if (change < 0) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className={`${sizeClasses[size]} ${className} relative`}>
      {showStatus && (
        <div className="font-medium" style={{ color: status.color }}>
          {status.message}
        </div>
      )}

      {/* Energy Change Tooltip */}
      {showChangeTooltip && energyChangeMessage && energyChange && (
        <div
          className={`mt-2 p-3 rounded-lg border ${getChangeColor(energyChange)} animate-fade-in`}
          role="alert"
        >
          <div className="flex items-start gap-2">
            <span className="text-xl flex-shrink-0">
              {energyChange > 0 ? <IoFlash /> : <IoTrendingDown />}
            </span>
            <div className="flex-1">
              <div className="font-semibold text-sm mb-1">
                Energy {energyChange > 0 ? 'Gained' : 'Lost'}: {Math.abs(energyChange)}%
              </div>
              <div className="text-sm leading-relaxed">
                {energyChangeMessage}
              </div>
            </div>
            <button
              onClick={() => {
                setShowChangeTooltip(false);
                onEnergyChangeAcknowledged?.();
              }}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Dismiss"
            >
              <IoClose />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergyDisplay;
